import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Typography,
  Stack,
  Skeleton,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DetailCard, CopyButton, Icons } from '@marageti/z4-lib';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import { format } from 'date-fns';
import { SalesRecordingStatus, SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { getCurrencyDetailsContent } from '../../../utils/helpers';
import { formatInvoiceMonth } from '../../../utils/sales';

const formatDate = (timestamp: string) => format(new Date(timestamp), 'MMM d, yyyy');

type ViewProps = {
  isLoading: boolean;
  repeatReferral: SalesRecording | undefined;
};

const RepeatReferralDetailsView = ({
  isLoading,
  repeatReferral,
}: ViewProps) => {
  const navigate = useNavigate();

  const getPageTitle = (salesRecordingType: SalesRecordingType) => {
    const types = {
      [SalesRecordingType.General]: 'Standard Sale',
      [SalesRecordingType.Adjustment]: 'Sales Adjustment',
      [SalesRecordingType.PartnerRepeat]: 'Repeat Sale',
      [SalesRecordingType.TravelerReferral]: 'Referral Sale',
      [SalesRecordingType.CommissionAfterTravel]: 'Commission After Travel',
      [SalesRecordingType.PlanningFee]: 'Planning Fee',
      [SalesRecordingType.RefundCredit]: 'Refund Credit',
    };
    return types[salesRecordingType] || '';
  };

  return (
    <Container
      maxWidth="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          py: { desktop: 7, mobile: 3 },
        }}
      >
        <Stack spacing={3}>
          {isLoading && (
            <>
              <Skeleton height={32} width={200} />
              <Skeleton height={252} width={774} />
              <Skeleton height={480} width={774} />
            </>
          )}
          {(!isLoading && repeatReferral) && (
            <>
              <IconButton
                aria-label="back to sales"
                onClick={() => navigate(-1)}
                sx={{
                  width: 'fit-content',
                  pl: 0,
                  color: 'primary.main',
                }}
                edge="start"
              >
                <Icons.ArrowLeft />
              </IconButton>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h2">{getPageTitle(repeatReferral.salesRecordingType)}</Typography>
              </Stack>
              {repeatReferral.status !== SalesRecordingStatus.SoldStatus && (
                <Alert severity="error">
                  <AlertTitle>Previously Invoiced</AlertTitle>
                  This recording has already been invoiced, and can no longer be edited. If changes are needed, the partner can submit a sales adjustment for the associated trip.
                </Alert>
              )}
              <DetailCard
                title="Traveler Info"
                items={[
                  { title: 'Name', content: repeatReferral.travelers?.[0]?.fullName },
                ]}
              />
              <DetailCard
                title="Sales Details"
                items={[
                  { title: 'Type', content: repeatReferral.salesRecordingType === SalesRecordingType.PartnerRepeat ? 'Repeat' : 'Referral' },
                  { title: 'Date Recorded', content: formatDate(repeatReferral.createdAt) },
                  { title: 'Total Trip Price', content: getCurrencyDetailsContent(repeatReferral.price) },
                  { title: 'Commission to Zicasso', content: getCurrencyDetailsContent(repeatReferral.commission) },
                  { title: 'Comments', content: repeatReferral.priceComments },
                  { title: 'Invoice Month', content: formatInvoiceMonth(repeatReferral?.invoiceDate!) },
                  {
                    title: 'Sales ID',
                    content: (
                      <>
                        {repeatReferral.id}
                        <CopyButton textToCopy={repeatReferral.id} iconFontSize="small" size="small" />
                      </>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default RepeatReferralDetailsView;
